import { inject, Injectable, NgZone } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../environments/environments';
import { references } from './references';
import { SessionStorageService } from './session-storage.service';

@Injectable({
  providedIn: 'root',
})
export class SignalWebsocketsService {
  #connection: HubConnection | null = null;
  #eventSubjects: { [key: string]: BehaviorSubject<any> } = {};
  #registeredEvents = new Set<string>();
  #storageService = inject(SessionStorageService);
  #zone = inject(NgZone);

  private isConnected = false;
  private currentBranchId: string | null = null;

  public connect(hubUrl: string): Observable<void> {
    const token = this.#storageService.load(references._STORAGE_TOKEN);
    if (!token) {
      return throwError(
        () => 'No access token found. Unable to connect to SignalR.',
      );
    }

    if (
      this.#connection &&
      this.#connection.state !== HubConnectionState.Disconnected
    ) {
      return this.ensureConnection();
    }

    this.#connection = new HubConnectionBuilder()
      .withUrl(`${environment.RESERVE_URL}/${hubUrl}`, {
        accessTokenFactory: () =>
          this.#storageService.load(references._STORAGE_TOKEN) ?? '',
        withCredentials: false,
      })
      .withAutomaticReconnect([0, 5000, 15000])
      .build();

    return from(this.#connection.start()).pipe(
      switchMap(() => {
        this.isConnected = true;
        Object.keys(this.#eventSubjects).forEach((e) =>
          this.registerEventHandler(e),
        );
        return from(Promise.resolve());
      }),
      catchError((err) => {
        console.error('❌ Erro ao conectar ao WebSocket:', err);
        return throwError(() => err);
      }),
    );
  }

  private ensureConnection(): Observable<void> {
    if (this.#connection?.state === HubConnectionState.Connected) {
      return from(Promise.resolve());
    }
    if (!this.#connection) {
      return throwError(
        () => 'Connection not established. Call connect() first.',
      );
    }

    return new Observable<void>((observer) => {
      const check = () => {
        if (this.#connection!.state === HubConnectionState.Connected) {
          observer.next();
          observer.complete();
        }
      };

      check();

      const onreconnected = () => check();
      const onclose = (err?: any) => observer.error(err ?? 'closed');

      this.#connection!.onreconnected(onreconnected);
      this.#connection!.onclose(onclose);

      const timeout = setTimeout(
        () => observer.error('Timeout waiting for reconnection'),
        10000,
      );

      return () => clearTimeout(timeout);
    });
  }

  private registerEventHandler(eventName: string): void {
    if (!this.#connection || this.#registeredEvents.has(eventName)) return;
    this.#registeredEvents.add(eventName);
    this.#connection.on(eventName, (data) => {
      this.#zone.run(() => this.#eventSubjects[eventName]?.next(data));
    });
  }

  public listenToEvent<T>(eventName: string): Observable<T> {
    if (!this.#eventSubjects[eventName]) {
      this.#eventSubjects[eventName] = new BehaviorSubject<T | null>(null);
    }
    this.registerEventHandler(eventName);
    return this.#eventSubjects[eventName].asObservable();
  }

  public removeEventListening(eventName: string): void {
    this.#connection?.off(eventName);
    this.#registeredEvents.delete(eventName);
    const subject = this.#eventSubjects[eventName];
    if (subject) {
      subject.complete?.();
      delete this.#eventSubjects[eventName];
    }
  }

  public invoke<T>(methodName: string, ...args: any[]): Observable<T> {
    return this.ensureConnection().pipe(
      switchMap(() => from(this.#connection!.invoke(methodName, ...args))),
      catchError((err) => {
        console.error(`❌ Erro ao invocar método ${methodName}:`, err);
        return throwError(() => err);
      }),
    );
  }

  public disconnect(): void {
    if (!this.#connection) return;

    try {
      Object.keys(this.#eventSubjects).forEach((eventName) => {
        try {
          this.#connection!.off(eventName);
        } catch {
          /* noop */
        }
        try {
          this.#eventSubjects[eventName].complete?.();
        } catch {
          /* noop */
        }
        delete this.#eventSubjects[eventName];
      });

      this.#connection.stop().catch((err) => {
        console.error('❌ Erro ao parar conexão WebSocket:', err);
      });
    } finally {
      this.isConnected = false;
      this.currentBranchId = null;
      this.#registeredEvents.clear();
      this.#connection = null;
    }
  }

  public connectToBranch(branchId: string): Observable<void> {
    this.currentBranchId = branchId;
    return this.invoke<void>('ConnectTo', `Branch:${branchId}`);
  }

  public disconnectFromBranch(): Observable<void> {
    if (!this.currentBranchId) return from(Promise.resolve());
    const id = this.currentBranchId;
    this.currentBranchId = null;
    return this.invoke<void>('DisconnectTo', `Branch:${id}`);
  }
}
